import {useContext, useEffect} from 'react';
import {Outlet, useLocation} from 'react-router-dom';
import {logEvent} from 'firebase/analytics';
import {getOptionalAnalytics} from '../firebaseApp';
import {AccessibilityContext} from 'contexts/accessibility/AccessibilityContext';
import {getAuth} from 'firebase/auth';
import {
  addDoc,
  collection,
  getFirestore,
  serverTimestamp,
} from 'firebase/firestore';
import {
  getOrCreateSessionIdWithMeta,
  getOrCreateDeviceIdWithMeta,
  getSessionJSON,
  setSessionJSON,
  getLocal,
  setLocal,
} from '@cip/core/utils';

/** Extract a named param from either the querystring or hash portion of a URL. */
function extractParam(
  name: string,
  search: string,
  hash: string,
): string | null {
  try {
    if (search) {
      const qs = new URLSearchParams(search);
      const v = qs.get(name);
      if (v) return v;
    }
    if (hash) {
      const stripped = hash.startsWith('#') ? hash.slice(1) : hash;
      const q = stripped.includes('?')
        ? stripped.slice(stripped.indexOf('?') + 1)
        : stripped;
      const hs = new URLSearchParams(q);
      const v = hs.get(name);
      if (v) return v;
    }
  } catch (e) {
    console.debug('[hotfix-r] param parsing failed', e);
  }
  return null;
}

/** Collect low-hanging client metadata for the analytics payload. */
function collectClientMetadata() {
  const isBrowser = typeof window !== 'undefined';
  const hasNav = typeof navigator !== 'undefined';
  const hasDoc = typeof document !== 'undefined';

  return {
    user_agent: hasNav ? navigator.userAgent : null,
    language: hasNav ? navigator.language : null,
    languages: hasNav ? (navigator.languages ?? []) : [],
    timezone: (() => {
      try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone ?? null;
      } catch {
        return null;
      }
    })(),
    referrer: hasDoc ? document.referrer || null : null,
    viewport_w: isBrowser ? window.innerWidth : null,
    viewport_h: isBrowser ? window.innerHeight : null,
    dpr:
      isBrowser && typeof window.devicePixelRatio === 'number'
        ? window.devicePixelRatio
        : null,
    screen_w: isBrowser && window.screen ? window.screen.width : null,
    screen_h: isBrowser && window.screen ? window.screen.height : null,
    screen_avail_w: isBrowser && window.screen ? window.screen.availWidth : null,
    screen_avail_h: isBrowser && window.screen
      ? window.screen.availHeight
      : null,
    color_depth: isBrowser && window.screen ? window.screen.colorDepth : null,
  };
}

/**
 * Extract a Firestore error code or message from an unknown caught value.
 * Avoids `as any` type assertions by narrowing with `instanceof`.
 */
function extractErrorInfo(err: unknown): string {
  if (err instanceof Error) {
    return ('code' in err && typeof (err as {code?: string}).code === 'string'
      ? (err as {code: string}).code
      : err.message);
  }
  return String(err);
}

const SEEN_KEY = 'cip_first_entry_logged_v1';
const COOLDOWN_KEY = 'cip_session_entry_cooldown_ms_v1';
const COOLDOWN_MS = 5 * 60 * 1000;

const CipAnalyticsWrapperRoute = () => {
  const location = useLocation();
  const {setPathname} = useContext(AccessibilityContext);

  useEffect(() => {
    try {
      const analytics = getOptionalAnalytics();
      if (!analytics) return;
      logEvent(analytics, 'screen_view');
    } catch (error) {
      console.trace('Error logging screen view', error);
    }
  }, [location.key]);

  // Temporary hotfix: log first-time page entry per session to Firestore analytics; include r and UTMs if present.
  useEffect(() => {
    const hasLogged = getSessionJSON<boolean>(SEEN_KEY, false);
    if (hasLogged) return;

    // Cross-tab cooldown: 5 minutes across this browser profile
    const nowMs = Date.now();
    const lastMsRaw = getLocal(COOLDOWN_KEY);
    const lastMs = lastMsRaw ? Number(lastMsRaw) : NaN;
    if (Number.isFinite(lastMs) && nowMs - lastMs < COOLDOWN_MS) {
      setSessionJSON(SEEN_KEY, true);
      return;
    }

    const get = (name: string) =>
      extractParam(name, location.search, location.hash);

    const run = async () => {
      try {
        const db = getFirestore();
        const uid = getAuth()?.currentUser?.uid ?? null;
        const {id: session_id, method: session_id_method} =
          getOrCreateSessionIdWithMeta();
        const {id: device_id, method: device_id_method} =
          getOrCreateDeviceIdWithMeta();

        const payload = {
          type: 'hotfix_session_entry',
          r: get('r') ?? null,
          utm_source: get('utm_source') ?? null,
          utm_medium: get('utm_medium') ?? null,
          utm_campaign: get('utm_campaign') ?? null,
          utm_term: get('utm_term') ?? null,
          utm_content: get('utm_content') ?? null,
          session_id,
          session_id_method,
          device_id,
          device_id_method,
          uid,
          signed_in: Boolean(uid),
          url: typeof window !== 'undefined' ? window.location.href : '',
          path: location.pathname,
          ...collectClientMetadata(),
          ts: serverTimestamp(),
        };

        try {
          await addDoc(collection(db, 'analytics', 'raw', 'data'), payload);
        } catch (err: unknown) {
          console.debug(
            '[hotfix-r] raw path write failed; attempting root analytics fallback',
            err,
          );
          try {
            await addDoc(collection(db, 'analytics'), {
              ...payload,
              innerErr: extractErrorInfo(err),
              outerErr: null,
            });
          } catch (err2: unknown) {
            console.debug(
              '[hotfix-r] root analytics fallback write failed',
              err2,
            );
            throw err2;
          }
        }
      } catch (e) {
        console.debug('[hotfix-r] analytics write failed', e);
      } finally {
        setSessionJSON(SEEN_KEY, true);
        setLocal(COOLDOWN_KEY, String(nowMs));
      }
    };

    run();
    // temporary hotfix listens to location changes; dedup prevents repeats
  }, [location.key, location.search, location.hash, location.pathname]);

  useEffect(() => {
    setPathname(location.pathname);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  }, [location.pathname]);
  return <Outlet />;
};

export default CipAnalyticsWrapperRoute;
